import { mergeClasses } from "@fluentui/react-components";
import { SidebarFooter } from "apps/project-iq/components/SidebarFooter/SidebarFooter";
import { SidebarHeader } from "apps/project-iq/components/SidebarHeader/SidebarHeader";
import { SidebarNavigation } from "apps/project-iq/components/SidebarNavigation/SidebarNavigation";
import { SidebarWorkspaceList } from "apps/project-iq/components/SidebarWorkspaceList/SidebarWorkspaceList";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { useMemo, useState } from "react";
import { useWorkspaceSidebarStyles } from "./WorkspaceSidebar.styles";
import { IWorkspaceSidebarProps } from "./WorkspaceSidebar.types";

const MAX_RECENT_WORKSPACES = 5;
const LS_COLLAPSED = "piq_sidebar_collapsed";

export const WorkspaceSidebar = ({
  workspaces,
  selectedWorkspaceId,
  isWorkspacesLoading,
  currentUser,
  pinnedWorkspaceIds,
  recentWorkspaceIds,
  onSelectWorkspace,
  onAddWorkspace,
  onViewAllWorkspaces,
  onTogglePin,
}: IWorkspaceSidebarProps): JSX.Element => {
  const styles = useWorkspaceSidebarStyles();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    () => localStorage.getItem(LS_COLLAPSED) === "true",
  );
  const [isSidebarHovered, setIsSidebarHovered] = useState<boolean>(false);
  const [hoveredWorkspaceId, setHoveredWorkspaceId] = useState<string | null>(
    null,
  );
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);

  const isVisuallyExpanded = !isCollapsed || isSidebarHovered || isUserMenuOpen;

  const pinnedWorkspaces = useMemo(
    () =>
      workspaces.filter((workspace) =>
        pinnedWorkspaceIds.includes(workspace.id),
      ),
    [workspaces, pinnedWorkspaceIds],
  );

  const recentWorkspaces = useMemo(() => {
    const workspacesById = new Map(
      workspaces.map((workspace) => [workspace.id, workspace]),
    );
    return recentWorkspaceIds
      .filter((id) => !pinnedWorkspaceIds.includes(id))
      .map((id) => workspacesById.get(id))
      .filter(
        (workspace): workspace is IProjectIQWorkspace =>
          workspace !== undefined,
      )
      .slice(0, MAX_RECENT_WORKSPACES);
  }, [workspaces, recentWorkspaceIds, pinnedWorkspaceIds]);

  const handleToggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    if (next) setIsSidebarHovered(false);
    localStorage.setItem(LS_COLLAPSED, String(next));
  };

  return (
    <aside
      className={mergeClasses(
        styles.sidebar,
        !isVisuallyExpanded && styles.sidebarCollapsed,
      )}
      onMouseEnter={() => setIsSidebarHovered(true)}
      onMouseLeave={() => setIsSidebarHovered(false)}
    >
      <SidebarHeader
        isCollapsed={isCollapsed}
        isVisuallyExpanded={isVisuallyExpanded}
        onToggleCollapse={handleToggleCollapse}
      />

      <SidebarNavigation isVisuallyExpanded={isVisuallyExpanded} />

      <div className={styles.scrollableContent}>
        <SidebarWorkspaceList
          pinnedWorkspaces={pinnedWorkspaces}
          recentWorkspaces={recentWorkspaces}
          totalWorkspaceCount={workspaces.length}
          selectedWorkspaceId={selectedWorkspaceId}
          hoveredWorkspaceId={hoveredWorkspaceId}
          isVisuallyExpanded={isVisuallyExpanded}
          isWorkspacesLoading={isWorkspacesLoading}
          onSelectWorkspace={onSelectWorkspace}
          onTogglePin={onTogglePin}
          onHoverWorkspace={setHoveredWorkspaceId}
          onAddWorkspace={onAddWorkspace}
          onViewAllWorkspaces={onViewAllWorkspaces}
        />
      </div>

      <SidebarFooter
        isVisuallyExpanded={isVisuallyExpanded}
        currentUser={currentUser}
        isUserMenuOpen={isUserMenuOpen}
        onUserMenuOpenChange={setIsUserMenuOpen}
      />
    </aside>
  );
};
